import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import { PG, pgBtn } from './theme';
import {
  NutritionRoute,
  filterNutritionRoutes,
  nutritionRouteAlias,
  nutritionRouteKey,
} from './nutritionRoutes';

export type { NutritionRoute };

type Props = {
  open: boolean;
  onClose: () => void;
  authHeaders: () => Promise<HeadersInit>;
  onUseRoute: (route: NutritionRoute) => void;
  pinnedIds?: string[];
  routes?: NutritionRoute[];
  source?: string;
  /** Defaults to the REST catalog. SDK mode points at /sdk/capabilities. */
  endpoint?: string;
  title?: string;
  blurb?: React.ReactNode;
  /**
   * SDK mode: show the wrapping SDK method as the headline instead of the REST path,
   * and mark routes the SDK does not wrap.
   */
  sdkMode?: boolean;
};

function methodColor(method: string): string {
  if (method === 'GET') return '#38bdf8';
  if (method === 'POST') return '#34d399';
  return PG.muted;
}

function SchemaBlock({ title, schema }: { title: string; schema: any }) {
  if (!schema) return null;
  let body = '';
  try {
    body = JSON.stringify(schema, null, 2);
  } catch {
    body = String(schema);
  }
  if (body.length > 1800) body = `${body.slice(0, 1800)}\n…`;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ color: PG.text, fontWeight: 700, display: 'block', mb: 0.35 }}>
        {title}
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1,
          borderRadius: 1,
          bgcolor: PG.chat,
          border: '1px solid',
          borderColor: PG.border,
          fontSize: 11,
          lineHeight: 1.4,
          overflow: 'auto',
          maxHeight: 220,
          color: PG.muted,
        }}
      >
        {body}
      </Box>
    </Box>
  );
}

export const NutritionApiBrowser: React.FC<Props> = ({
  open,
  onClose,
  authHeaders,
  onUseRoute,
  pinnedIds = [],
  routes: routesProp,
  source: sourceProp,
  endpoint = '/api/v2/playground/nutrition/openapi',
  title = 'NutritionAI routes',
  blurb,
  sdkMode = false,
}) => {
  const [q, setQ] = useState('');
  const [tag, setTag] = useState('all');
  const [routesLocal, setRoutesLocal] = useState<NutritionRoute[]>([]);
  const [sourceLocal, setSourceLocal] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);

  const routes = routesProp?.length ? routesProp : routesLocal;
  const source = sourceProp || sourceLocal;

  useEffect(() => {
    if (!open || (routesProp && routesProp.length)) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const headers = await authHeaders();
        const res = await fetch(endpoint, { headers });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.detail || 'Could not load Nutrition API catalog');
        if (cancelled) return;
        setSourceLocal(body.source || '');
        setRoutesLocal(Array.isArray(body.routes) ? body.routes : []);
      } catch (e: any) {
        if (!cancelled) setErr(e.message || 'Could not load Nutrition API catalog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, authHeaders, routesProp, endpoint]);

  const tags = useMemo(() => {
    const set = new Set<string>();
    routes.forEach((r) => (r.tags || []).forEach((t) => set.add(t)));
    return ['all', ...Array.from(set).sort()];
  }, [routes]);

  const filtered = useMemo(() => filterNutritionRoutes(routes, q, tag), [routes, q, tag]);
  const pinned = useMemo(() => new Set(pinnedIds), [pinnedIds]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { bgcolor: PG.panel, color: PG.text, maxHeight: '86vh' } }}
    >
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        {title}
        <Typography variant="body2" sx={{ color: PG.muted, fontWeight: 400, mt: 0.5, lineHeight: 1.45 }}>
          {blurb || (
            <>
              Live OpenAPI catalog. Add a route to this chat to inject its exact request and response
              fields. You can also type <strong>@route</strong> in the composer.
            </>
          )}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: PG.muted }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1.5 }}>
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder={
            sdkMode
              ? 'Search searchForFoodSemantic, barcode, recognize, meal plan…'
              : 'Search scan, semantic, refCode, affiliate, barcode…'
          }
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, mb: 1, flexWrap: 'wrap', gap: 0.75 }}>
          {tags.map((t) => (
            <Chip
              key={t}
              size="small"
              label={t === 'all' ? 'All' : t}
              onClick={() => setTag(t)}
              sx={{
                fontWeight: tag === t ? 800 : 500,
                bgcolor: tag === t ? 'rgba(52,211,153,0.22)' : 'transparent',
                color: PG.text,
                border: '1px solid',
                borderColor: tag === t ? 'rgba(52,211,153,0.55)' : PG.border,
              }}
            />
          ))}
        </Stack>
        {loading && (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        )}
        {err && (
          <Typography variant="body2" sx={{ color: '#fbbf24', py: 2 }}>
            {err}
          </Typography>
        )}
        {!loading && !err && (
          <List dense disablePadding sx={{ mx: -1 }}>
            {filtered.length === 0 && (
              <Typography variant="body2" sx={{ color: PG.muted, px: 1, py: 2 }}>
                No routes match.
              </Typography>
            )}
            {filtered.map((r) => {
              const key = `${r.method}:${r.path}`;
              const id = nutritionRouteKey(r);
              const openRow = picked === key;
              const isPinned = pinned.has(id);
              return (
                <Box key={key} sx={{ borderBottom: '1px solid', borderColor: PG.border }}>
                  <ListItemButton onClick={() => setPicked(openRow ? null : key)} sx={{ alignItems: 'flex-start', py: 1 }}>
                    {sdkMode ? (
                      <Chip
                        size="small"
                        label={r.sdk_covered ? 'SDK' : 'REST'}
                        sx={{
                          mr: 1.25,
                          mt: 0.15,
                          minWidth: 54,
                          fontWeight: 800,
                          bgcolor: 'transparent',
                          color: r.sdk_covered ? '#a78bfa' : PG.muted,
                          border: '1px solid',
                          borderColor: r.sdk_covered ? '#a78bfa' : PG.border,
                        }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label={r.method}
                        sx={{
                          mr: 1.25,
                          mt: 0.15,
                          minWidth: 54,
                          fontWeight: 800,
                          bgcolor: 'transparent',
                          color: methodColor(r.method),
                          border: '1px solid',
                          borderColor: methodColor(r.method),
                        }}
                      />
                    )}
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          {/* In SDK mode the method name is what the developer is looking for;
                              the REST path is detail, so it drops to the secondary line. */}
                          <span>{sdkMode ? r.sdk_method || r.summary : r.full_path}</span>
                          {isPinned && (
                            <Chip size="small" label="In chat" sx={{ height: 20, bgcolor: 'rgba(52,211,153,0.2)', color: PG.text }} />
                          )}
                        </Stack>
                      }
                      secondary={
                        sdkMode
                          ? r.sdk_covered
                            ? `${r.summary} · wraps ${r.path}`
                            : `${r.summary} · no SDK method — call REST`
                          : `${r.summary}${r.alias ? `  @${r.alias}` : ''}`
                      }
                      primaryTypographyProps={{ fontSize: 13, fontWeight: 700, sx: { wordBreak: 'break-all' }, component: 'div' }}
                      secondaryTypographyProps={{ fontSize: 12, color: PG.muted, sx: { mt: 0.25 } }}
                    />
                  </ListItemButton>
                  {openRow && (
                    <Box sx={{ px: 2, pb: 1.5, pl: 9 }}>
                      {r.description && (
                        <Typography variant="body2" sx={{ color: PG.muted, lineHeight: 1.5, mb: 1 }}>
                          {r.description}
                        </Typography>
                      )}
                      {r.notes && (
                        <Typography variant="body2" sx={{ color: '#fbbf24', lineHeight: 1.45, mb: 1 }}>
                          {r.notes}
                        </Typography>
                      )}
                      {sdkMode && r.sdk_note && (
                        <Typography variant="body2" sx={{ color: '#fbbf24', lineHeight: 1.45, mb: 1 }}>
                          {r.sdk_note}
                        </Typography>
                      )}
                      {sdkMode && r.sdk_covered && (
                        <Typography variant="body2" sx={{ color: PG.text, lineHeight: 1.5, mb: 1 }}>
                          SDK method: <strong>{r.sdk_method}</strong>. The fields below are the REST
                          shape the SDK wraps — same data, platform-native names.
                        </Typography>
                      )}
                      {(r.params || []).length > 0 && (
                        <Typography variant="caption" sx={{ display: 'block', color: PG.text, mb: 0.5 }}>
                          Params:{' '}
                          {(r.params || [])
                            .map((p) => `${p.in}:${p.name}${p.required ? '*' : ''}`)
                            .join(', ')}
                        </Typography>
                      )}
                      <SchemaBlock title="Request" schema={r.request || (r.body ? { name: r.body } : null)} />
                      {r.example != null && <SchemaBlock title="Example" schema={r.example} />}
                      <SchemaBlock title="Response 200" schema={r.response} />
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Button
                          size="small"
                          variant="contained"
                          sx={{ ...pgBtn, bgcolor: '#34d399', color: '#042f2e', fontWeight: 800 }}
                          onClick={() => onUseRoute(r)}
                        >
                          {isPinned ? 'Added — add @ again' : 'Add to chat'}
                        </Button>
                        {r.docs_url && (
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                            href={r.docs_url}
                            target="_blank"
                            rel="noreferrer"
                            sx={{ ...pgBtn, color: PG.text, borderColor: PG.border }}
                          >
                            Full docs
                          </Button>
                        )}
                      </Stack>
                      <Typography variant="caption" sx={{ display: 'block', color: PG.muted, mt: 0.75 }}>
                        {sdkMode
                          ? `Pins @${nutritionRouteAlias(r)} so the next send knows which SDK call to write and what it returns.`
                          : `Pins @${nutritionRouteAlias(r)} so the next send includes this route’s inputs and outputs.`}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.25, justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: PG.muted, maxWidth: '70%' }} noWrap>
          {source || 'cdn.passiolife.com/documentation/openapi_manual.json'}
        </Typography>
        <Button onClick={onClose} sx={{ ...pgBtn, color: PG.text }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
